
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { useAIAnalysis } from "@/hooks/useAIAnalysis";
import LoadingState from "@/components/ai-analysis/LoadingState";
import EmptyState from "@/components/ai-analysis/EmptyState";
import AnalysisHeader from "@/components/ai-analysis/AnalysisHeader";
import GlobalAnalysisCard from "@/components/ai-analysis/GlobalAnalysisCard";
import LevelAnalysisCard from "@/components/ai-analysis/LevelAnalysisCard";
import ExerciseAnalysisCard from "@/components/ai-analysis/ExerciseAnalysisCard";
import ActionPlanCard from "@/components/ai-analysis/ActionPlanCard";
import AnalysisActions from "@/components/ai-analysis/AnalysisActions";
import AIQuestionChat from "@/components/ai-analysis/AIQuestionChat";

const AIAnalysis = () => {
  const navigate = useNavigate();
  const { 
    isAnalyzing, 
    analysisResult,
    analysisDate,
    analyzeTrainingData, 
    canAnalyze, 
    isLoading,
    setAnalysisResult,
    setAnalysisDate
  } = useAIAnalysis();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate]);

  const handleBackToHome = () => {
    navigate("/");
  };

  if (isAnalyzing) {
    return <LoadingState />;
  }

  if (!analysisResult) {
    return (
      <EmptyState 
        onBackToHome={handleBackToHome}
        onAnalyzeData={analyzeTrainingData}
        onSelectPreviousAnalysis={(analysis, date) => {
          setAnalysisResult(analysis);
          setAnalysisDate(date);
        }}
        canAnalyze={canAnalyze}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      {/* Navigation masquée lors de l'impression */}
      <div className="print:hidden">
        <NavigationMenu />
      </div>
      
      <div className="container mx-auto p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <AnalysisHeader onBackToHome={handleBackToHome} analysisDate={analysisDate} />
          
          <GlobalAnalysisCard globalAnalysis={analysisResult.globalAnalysis} />
          
          <LevelAnalysisCard levelAnalysis={analysisResult.levelAnalysis} />
          
          <ExerciseAnalysisCard exerciseAnalysis={analysisResult.exerciseAnalysis} />
          
          <ActionPlanCard actionPlan={analysisResult.actionPlan} />
          
          <AIQuestionChat analysisResult={analysisResult} />
          
          <AnalysisActions 
            onBackToHome={handleBackToHome}
            onSelectPreviousAnalysis={(analysis, date) => {
              setAnalysisResult(analysis);
              setAnalysisDate(date);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AIAnalysis;
